export type EvolysRuntimeConfigKey =
  | "MICROBIOLOGY_URL"
  | "PATHOLOGY_URL"
  | "ADMIN_URL"
  | "LAB_HUB_URL"
  | "OPERATIONS_URL"
  | "OPERATIONS_ORDER_URL"
  | "EMR_CONTROL_API_URL"
  | "SESSION_TIMEOUT_MINUTES"
  | "SENTRY_DSN"
  | "SENTRY_ENVIRONMENT"
  | "SENTRY_RELEASE"
  | "SENTRY_TRACES_SAMPLE_RATE";

declare global {
  interface Window {
    __EVOLYS_CONFIG__?: Partial<Record<EvolysRuntimeConfigKey, string>>;
  }
}

export function getRuntimeConfig(
  key: EvolysRuntimeConfigKey,
  buildValue: string | undefined,
  fallback: string,
): string {
  const runtimeValue =
    typeof window === "undefined"
      ? undefined
      : window.__EVOLYS_CONFIG__?.[key];
  return runtimeValue?.trim() || buildValue?.trim() || fallback;
}

interface BrowserLocation {
  protocol: string;
  hostname: string;
  port?: string;
}

export function inferOperationsOrigin(
  location: BrowserLocation,
): string {
  const hostname = location.hostname.trim().toLowerCase();
  if (!hostname) return "";

  if (hostname === "localhost" || hostname === "127.0.0.1") {
    return "http://localhost:3000";
  }

  const labels = hostname.split(".");
  if (labels.length < 2) return "";

  if (labels.length === 2) {
    labels.unshift("operations");
  } else if (labels[0] !== "operations") {
    labels[0] = "operations";
  }

  const port = location.port ? `:${location.port}` : "";
  return `${location.protocol}//${labels.join(".")}${port}`;
}

function currentOperationsFallback(): string {
  if (typeof window === "undefined") return "";
  return inferOperationsOrigin(window.location);
}

export function getOperationsUrl(buildValue?: string): string {
  return getRuntimeConfig(
    "OPERATIONS_URL",
    buildValue,
    currentOperationsFallback(),
  ).replace(/\/$/, "");
}

export function getOperationsOrderUrl(
  orderBuildValue?: string,
  operationsBuildValue?: string,
): string {
  const operationsOrigin = getOperationsUrl(operationsBuildValue);
  const configuredOrderUrl = getRuntimeConfig(
    "OPERATIONS_ORDER_URL",
    orderBuildValue,
    "",
  );

  if (configuredOrderUrl) {
    try {
      return new URL(
        configuredOrderUrl,
        operationsOrigin ? `${operationsOrigin}/` : undefined,
      ).toString();
    } catch {
      // Fall back to the canonical Operations route below.
    }
  }

  return operationsOrigin ? `${operationsOrigin}/orders/new` : "";
}
